import type {
  LogicalRange,
  SeriesMarker,
  UTCTimestamp,
} from "lightweight-charts";

import { formatPrice } from "./format";

interface ExtremaCandle {
  time: UTCTimestamp;
  high: number;
  low: number;
}

function visibleIndexes(
  length: number,
  range: LogicalRange | null,
): [number, number] | null {
  if (length === 0) return null;
  if (!range) return [0, length - 1];

  const first = Math.max(0, Math.ceil(range.from));
  const last = Math.min(length - 1, Math.floor(range.to));
  if (first <= last) return [first, last];

  const nearest = Math.max(
    0,
    Math.min(length - 1, Math.round((range.from + range.to) / 2)),
  );
  return [nearest, nearest];
}

export function buildExtremaMarkers(
  candles: readonly ExtremaCandle[],
  range: LogicalRange | null,
): SeriesMarker<UTCTimestamp>[] {
  const indexes = visibleIndexes(candles.length, range);
  if (!indexes) return [];

  const [first, last] = indexes;
  let highest = candles[first];
  let lowest = candles[first];

  for (let index = first + 1; index <= last; index += 1) {
    const candle = candles[index];
    if (candle.high > highest.high) highest = candle;
    if (candle.low < lowest.low) lowest = candle;
  }

  const markers: SeriesMarker<UTCTimestamp>[] = [
    {
      id: "visible-high",
      time: highest.time,
      position: "aboveBar",
      shape: "arrowDown",
      color: "#26333b",
      text: `H ${formatPrice(highest.high)}`,
      size: 0.8,
    },
    {
      id: "visible-low",
      time: lowest.time,
      position: "belowBar",
      shape: "arrowUp",
      color: "#26333b",
      text: `L ${formatPrice(lowest.low)}`,
      size: 0.8,
    },
  ];
  return markers.sort((left, right) => Number(left.time) - Number(right.time));
}
